<template>
    <div class="pa-5">
        <v-card v-if="data">
            <v-card-title>
                <h1 class="title">{{ 'Meeting Guide Tasks' | term }}</h1>
                <v-btn small color="secondary" class="ml-3" @click="add()">
                    <v-icon left>mdi-plus</v-icon>Add
                </v-btn>
                <v-btn small color="primary" class="ml-3" v-if="dirty" @click="save()" :loading="$wait.any">
                    <v-icon left>mdi-check</v-icon>Save
                </v-btn>
                <v-btn small color="grey" class="ml-3" v-if="dirty" @click="loadData()" :loading="$wait.any">
                    <v-icon left>mdi-close</v-icon>Cancel
                </v-btn>
                <v-spacer></v-spacer>
                <v-text-field
                    v-model="search"
                    append-icon="mdi-magnify"
                    label="Search"
                    single-line
                    hide-details
                    clearable
                    class="admin-table-filter"
                ></v-text-field>
            </v-card-title>
            <standard-wrapping-reorderable
                :items="filtered"
                :item-id="'id'"
                :item-order="'displayOrder'"
                @reordered="onItemsReordered"
                item-class="meeting-guide-item"
                handle-class="meeting-guide-handle"
                :disabled="!!search"
            >
                <v-data-table 
                    disable-pagination 
                    hide-default-footer 
                    :headers="headers" 
                    :items="filtered"
                    :search="search"
                >
                    <template v-slot:item="{ item }">
                        <tr :class="{ dirty: item.dirty, 'meeting-guide-item': true }" :key="`${item.id}-${item.dirty}`">
                            <td>
                                <div class="no-wrap">
                                    <v-icon class="meeting-guide-handle">mdi-drag</v-icon>
                                </div>
                            </td>
                            <td>
                                <v-textarea
                                    v-model="item.question"
                                    @keyup="item.dirty = true"
                                    class="mt-3"
                                    rows="1"
                                    auto-grow
                                ></v-textarea>
                            </td>
                            <td>
                                <v-checkbox v-model="item.isShortAnswer" @change="item.dirty = true"></v-checkbox>
                            </td>
                            <td>
                                <v-checkbox v-model="item.forShiftDeployment" @change="item.dirty = true"></v-checkbox>
                            </td>
                            <td>
                                <v-checkbox v-model="item.forShiftHandover" @change="item.dirty = true"></v-checkbox>
                            </td>
                            <td>
                                <v-checkbox v-model="item.forShiftCaptain" @change="item.dirty = true"></v-checkbox>
                            </td>
                            <td>
                                <v-access-control>
                                    <div class="no-wrap">
                                        <v-btn text icon @click="del(item)">
                                            <v-icon>mdi-delete</v-icon>
                                        </v-btn>
                                    </div>
                                </v-access-control>
                            </td>
                        </tr>
                    </template>
                </v-data-table>
            </standard-wrapping-reorderable>
        </v-card>
        <confirm ref="confirm"></confirm>
    </div>
</template>
<script>
import { EventBus, Events } from '@/lib/EventBus';
import MeetingGuide from '@/lib/data/MeetingGuide';
import StandardWrappingReorderable from '@/components/Shared/Common/StandardWrappingReorderable.vue';

export default {
    components: { StandardWrappingReorderable },
    data() {
        return {
            data: null,
            search: '',
            selected: null,
            showModal: false,
            headers: [
                { text: '', value: 'handle', width: '24px', align: 'left' },
                { text: 'Question', value: 'question', width: '75%' },
                { text: 'Text?', value: '', width: '5%', sortable: false },
                { text: 'Shift Deployment', value: 'forShiftDeployment', width: '5%' },
                { text: 'Shift Handover', value: 'forShiftHandover', width: '5%' },
                { text: 'Shift Interactions', value: 'forShiftCaptain', width: '5%' },
                { text: '', value: 'id', width: '10%', align: 'right' }
            ],
            reordered: false,
        };
    },
    async created() {
        await this.loadData();
    },
    destroyed() {},
    methods: {
        async loadData() {
            this.$wait.start('get data');
            this.reordered = false;
            try {
                var d = await MeetingGuide.get();
                for (let x of d) {
                    x.dirty = false;
                }
                this.data = d;
            } finally {
                this.$wait.end('get data');
            }
        },
        add() {
            var displayOrder = this.filtered.length ? this.filtered[this.filtered.length - 1].displayOrder + 1 : 1;
            this.data.push({ dirty: true, displayOrder: displayOrder });
        },
        async save() {
            this.$wait.start('save');
            try {
                for (let x of this.data) {
                    if (!x.dirty) {
                        continue;
                    }
                    await MeetingGuide.save(x);
                }
                EventBus.$emit(Events.ToastSuccess, `${this.$termSync('Meeting Guide')} Saved`);
                await this.loadData();
                EventBus.$emit(Events.TermsUpdated);
            } finally {
                this.$wait.end('save');
            }

        },
        async del(item) {
            if (!item.id) {
                this.data.splice(this.data.indexOf('foo'), 1);
                return;
            }
            if (await this.$refs.confirm.openAsDeleteResource(this.$termSync('Meeting Guide'), { })) {
                await MeetingGuide.delete(item.id);
                EventBus.$emit(Events.ToastSuccess, `${this.$termSync('Meeting Guide')} Deleted`);
                await this.save();
            }
        },
        onItemsReordered(reorderedItems) {
            // Update display orders based on new order
            reorderedItems.forEach((item, index) => {
                const originalItem = this.data.find(x => x.id === item.id);
                if (originalItem && originalItem.displayOrder !== index + 1) {
                    originalItem.displayOrder = index + 1;
                    originalItem.dirty = true;
                    this.reordered = true;
                }
            });
        },
    },
    computed: {
        filtered() {
            if (!this.data) {
                return [];
            }
            return this.data.filter(x => true).sort((a, b) => a.displayOrder - b.displayOrder);
        },

        dirty() {
            for (let x of this.data) {
                if (x.dirty) {
                    return true;
                }
            }
            return false;
        }
    }
};
</script>
